import axios from 'axios';

const DEFAULT_FILENAME = 'report.pdf';
const PDF_ACCEPT_HEADER = 'application/pdf, application/json';
const JSON_CONTENT_TYPE = 'application/json';
const SIGNED_URL_RETRY_STATUSES = [400, 401, 403];
const DISPOSITION_FILE_NAME_REGEX = /filename\*?=(?:UTF-8'')?["']?([^\"';]+)["']?/i;
const EXPIRED_SIGNED_URL_REGEX = /ExpiredToken|Request has expired|SignatureDoesNotMatch/i;

const ensurePdfExtension = (name) => {
  if (!name) {
    return DEFAULT_FILENAME;
  }
  const trimmed = name.trim();
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
};

const decodeSafely = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const extractFileName = (disposition, fallback) => {
  if (!disposition) {
    return ensurePdfExtension(fallback);
  }

  const match = DISPOSITION_FILE_NAME_REGEX.exec(disposition);
  const decoded = decodeSafely(match ? match[1] : fallback);

  return ensurePdfExtension(decoded);
};

const extractHeader = (headers = {}, key) => {
  if (!headers) {
    return undefined;
  }
  return headers[key] || headers[key.toLowerCase()] || headers[key.toUpperCase()];
};

const tryParseDownloadUrl = async (blob) => {
  if (!blob || typeof blob.text !== 'function') {
    return undefined;
  }

  try {
    const text = await blob.text();
    const payload = JSON.parse(text);
    return payload && typeof payload.url === 'string' ? payload.url : undefined;
  } catch (error) {
    return undefined;
  }
};

const isExpiredSignedUrlResponse = (status, payloadText = '') => {
  if (!SIGNED_URL_RETRY_STATUSES.includes(status)) {
    return false;
  }
  return EXPIRED_SIGNED_URL_REGEX.test(payloadText);
};

const fetchPdfByUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    const errorText = await response.text();
    const error = new Error(`Unable to download PDF by URL. Status: ${response.status}`);
    error.status = response.status;
    error.payloadText = errorText || '';
    error.isExpiredSignedUrl = isExpiredSignedUrlResponse(response.status, errorText);
    throw error;
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || undefined;

  return { blob, disposition };
};

const resolvePdfPayload = async ({ contentType, dispositionHeader, data }) => {
  if (!contentType.includes(JSON_CONTENT_TYPE)) {
    return {
      blob: new Blob([data], { type: 'application/pdf' }),
      resolvedDisposition: dispositionHeader,
    };
  }

  const downloadUrl = await tryParseDownloadUrl(data);
  if (!downloadUrl) {
    throw new Error('Invalid JSON response for PDF download endpoint');
  }

  const pdfByUrl = await fetchPdfByUrl(downloadUrl);
  return {
    blob: pdfByUrl.blob,
    resolvedDisposition: pdfByUrl.disposition || dispositionHeader,
  };
};

const isExpiredSignedUrlError = (error) => {
  if (error?.isExpiredSignedUrl) {
    return true;
  }
  if (
    SIGNED_URL_RETRY_STATUSES.includes(error?.status) &&
    EXPIRED_SIGNED_URL_REGEX.test(error?.payloadText || '')
  ) {
    return true;
  }
  return false;
};

export const downloadPdfReport = async ({
  axiosInstance = axios,
  url,
  data,
  onStart,
  onFinally,
  onError,
  defaultFileName = DEFAULT_FILENAME,
} = {}) => {
  const requestPdfResponse = () =>
    axiosInstance.post(url, data, {
      responseType: 'blob',
      headers: {
        Accept: PDF_ACCEPT_HEADER,
      },
    });

  const downloadFromResponse = async (response) => {
    const dispositionHeader = extractHeader(response?.headers, 'content-disposition');
    const contentType = extractHeader(response?.headers, 'content-type') || '';
    const { blob, resolvedDisposition } = await resolvePdfPayload({
      contentType,
      dispositionHeader,
      data: response.data,
    });

    const fileName = extractFileName(resolvedDisposition, defaultFileName);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(link.href);
    return fileName;
  };

  try {
    if (onStart) onStart();
    const firstResponse = await requestPdfResponse();
    try {
      return await downloadFromResponse(firstResponse);
    } catch (error) {
      if (!isExpiredSignedUrlError(error)) {
        throw error;
      }
      const refreshedResponse = await requestPdfResponse();
      return await downloadFromResponse(refreshedResponse);
    }
  } catch (error) {
    if (onError) onError(error);
    throw error;
  } finally {
    if (onFinally) onFinally();
  }
};
